import React, { type ReactNode } from 'react'
import { Box, Link } from '@mui/material'

import { type BannerConfig } from '~components/layout/bannerConfig'
import { borderRadius, spacing, typography } from '~tokens/internal'

import Banner from '../Banner'
import Icon from '../Icon'
import IconButton from '../IconButton'
import Typography from '../Typography'

import './BannerDisplay.scss'
import useBanners from './useBanners'

const renderBannerContent = (banner: BannerConfig): ReactNode => {
  const { message, links } = banner

  if (!links || links.length === 0) {
    return message
  }

  let content = message as string
  const elements: ReactNode[] = []
  let lastIndex = 0

  links.forEach((link, i) => {
    const linkText = link.text
    const linkIndex = content.indexOf(linkText, lastIndex)

    if (linkIndex !== -1) {
      elements.push(content.substring(lastIndex, linkIndex))

      elements.push(
        <Link
          key={i}
          href={link.url}
          target='_blank'
          rel='noopener noreferrer'
          className='banner-display__link'
        >
          {linkText}
        </Link>,
      )

      lastIndex = linkIndex + linkText.length
    }
  })

  elements.push(content.substring(lastIndex))

  return elements
}

interface BannerDisplayProps {
  banners: BannerConfig[]
  className?: string
  style?: React.CSSProperties
}

const BannerDisplay = ({ banners, className, style }: BannerDisplayProps): ReactNode => {
  const {
    activeBanners,
    currentBannerIndex,
    currentBanner,
    handleDismissBanner,
    goToNextBanner,
    goToPrevBanner,
  } = useBanners(banners)

  if (!currentBanner) return null

  const body = (
    <>
      {renderBannerContent(currentBanner)}
      {currentBanner.subtitle && (
        <Typography
          sx={{
            fontWeight: typography.fontWeight.regular,
            fontSize: typography.fontSize.xs,
            lineHeight: typography.lineHeight.normal,
          }}
        >
          {currentBanner.subtitle}
        </Typography>
      )}
    </>
  )

  const action =
    activeBanners.length > 1 ? (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: spacing.xxs }}>
        <IconButton size='small' onClick={goToPrevBanner}>
          <Icon name='chevronLeft' />
        </IconButton>
        <Typography
          sx={{
            fontSize: typography.fontSize.xxs,
            lineHeight: typography.lineHeight.normal,
          }}
        >
          {currentBannerIndex + 1}/{activeBanners.length}
        </Typography>
        <IconButton size='small' onClick={goToNextBanner}>
          <Icon name='chevronRight' />
        </IconButton>
      </Box>
    ) : undefined

  return (
    <div className={className} style={style}>
      <Banner
        key={currentBanner.id}
        type={currentBanner.type}
        body={body}
        dismissable={currentBanner.dismissible}
        onDismiss={handleDismissBanner}
        icon={currentBanner.icon}
        action={action}
        sx={{ borderRadius: borderRadius.none, ...currentBanner.sx }}
      />
    </div>
  )
}

export default BannerDisplay
